import { LABEL } from "../../../../utils/samplingDisplayLabels";

/** Shared Plotly legend traces for dashed reference lines (visible swatch in legend). */
export const STANDARD_WR_TRACE_NAME = LABEL.STANDARD_WR_TRACE;
export const IDENTITY_REFERENCE_TRACE_NAME = "1:1 (actual = standard)";

const REF_LINE = {
  color: "#e74c3c",
  dash: "dash",
  width: 2,
};

/**
 * Horizontal WR = 100% line for numeric x (length) axes.
 * @param {number} xMin
 * @param {number} xMax
 */
export function wrHundredLegendTraceNumeric(xMin, xMax) {
  if (!Number.isFinite(xMin) || !Number.isFinite(xMax)) return null;
  const left = xMax > xMin ? xMin : xMin - 0.5;
  const right = xMax > xMin ? xMax : xMax + 0.5;
  return {
    type: "scatter",
    mode: "lines",
    name: STANDARD_WR_TRACE_NAME,
    x: [left, right],
    y: [1, 1],
    line: { ...REF_LINE },
    hoverinfo: "skip",
    legendgroup: "_wrReference",
  };
}

/**
 * Horizontal WR = 100% for category x (dates, bin labels).
 * With a single category, Plotly cannot draw a zero-length line — use
 * {@link wrHundredCategoryFallbackShapes} for the visible stroke.
 * @param {string[]} categoryLabels
 * @param {object} [extra] — e.g. `{ yaxis: "y2" }`
 */
export function wrHundredLegendTraceCategory(categoryLabels, extra = {}) {
  if (!Array.isArray(categoryLabels) || categoryLabels.length === 0) return null;
  const x0 = categoryLabels[0];
  const x1 = categoryLabels[categoryLabels.length - 1];
  return {
    type: "scatter",
    mode: "lines",
    name: STANDARD_WR_TRACE_NAME,
    x: [x0, x1],
    y: [1, 1],
    line: { ...REF_LINE },
    hoverinfo: "skip",
    legendgroup: "_wrReference",
    ...extra,
  };
}

/**
 * Full-width WR = 100% stroke when the category axis has only one slot
 * (scatter [x0,x0]→[1,1] has zero length and does not paint).
 * @param {string[]} categoryLabels
 * @param {object} [extra] — e.g. `{ yaxis: "y2" }`
 * @returns {object[]|undefined}
 */
export function wrHundredCategoryFallbackShapes(categoryLabels, extra = {}) {
  if (!Array.isArray(categoryLabels) || categoryLabels.length !== 1) return undefined;
  const yref = extra.yaxis === "y2" ? "y2" : "y";
  return [
    {
      type: "line",
      xref: "paper",
      x0: 0,
      x1: 1,
      yref,
      y0: 1,
      y1: 1,
      line: {
        color: REF_LINE.color,
        width: REF_LINE.width,
        dash: REF_LINE.dash,
      },
      layer: "below",
    },
  ];
}

/** y = x identity line for standard vs actual charts. */
export function identityReferenceLegendTrace(rangeStart, rangeEnd) {
  if (!Number.isFinite(rangeStart) || !Number.isFinite(rangeEnd)) return null;
  return {
    type: "scatter",
    mode: "lines",
    name: IDENTITY_REFERENCE_TRACE_NAME,
    x: [rangeStart, rangeEnd],
    y: [rangeStart, rangeEnd],
    line: { ...REF_LINE },
    hoverinfo: "skip",
    legendgroup: "_identityReference",
  };
}

export function isWrReferenceTraceName(name) {
  return (
    name === STANDARD_WR_TRACE_NAME ||
    name === LABEL.STANDARD_WR_TRACE_PLAIN ||
    name === "Standard (WR = 100%)" ||
    name === "Standard (Wr = 100%)" ||
    name === "Standard (W<sub>R</sub> = 100%)"
  );
}

export function isIdentityReferenceTraceName(name) {
  return name === IDENTITY_REFERENCE_TRACE_NAME;
}
